import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { SigangeBarController } from '@/components/charts/barChartUtils';

/**
 * The Chart.js pieces this app actually draws with, registered explicitly and exactly once.
 *
 * Legacy `DoughnutChart.vue` called `Chart.register(...registerables)` at module scope, which
 * pulls in every controller, element, scale and plugin Chart.js ships — line, radar, bubble,
 * polar area, the radial and time scales — for an app that draws two chart types. Registering
 * the pieces by name is what keeps them out of the bundle.
 *
 * It also fixes a latent legacy fault worth naming, because it explains why this module exists
 * rather than a `register()` call inside each chart component: `barChart.vue` registered only
 * `SigangeBarController`, and relied on `DoughnutChart.vue` having been imported *somewhere on
 * the page* for its scales and plugins. A config with an `HourlyChartWidget` and no
 * `DoughnutWidget` would have drawn a bar chart with no scales at all. All four deployed
 * configs happen to carry both.
 *
 * ## Why `ChartDataLabels` is not here
 *
 * `chartjs-plugin-datalabels` is registered **per chart**, inline, by `DoughnutChart` — never
 * globally. That is legacy behaviour and it is load-bearing:
 *
 * - the legacy doughnut passed it as an inline plugin (`plugins: [textPlugin]`), and only when
 *   `chartConfig.chartText` is absent (docs/MIGRATION-WIDGETS.md §6, the exclusivity rule);
 * - the legacy bar chart carries an `options.plugins.datalabels` block that has **never had
 *   any effect**, because the plugin was never registered globally and the bar chart does not
 *   pass it inline. Its value labels come from `SigangeBarController.draw()` instead.
 *
 * Registering datalabels globally here would switch that dead config block on and paint a
 * second set of numbers onto every bar of four deployed displays. SYM-496's brief lists
 * `ChartDataLabels` among the pieces to register; this is the one deliberate departure from
 * that list, and it is recorded in `docs/reports/SYM-496.md`.
 */

Chart.register(
  // Doughnut.
  DoughnutController,
  ArcElement,
  // Bar. `BarElement` is the element type `SigangeBarController` inherits from `BarController`;
  // the two scales are what its `overrides` name as the defaults for `x` and `y`.
  SigangeBarController,
  BarElement,
  CategoryScale,
  LinearScale,
  // Plugins the deployed configs address by name: `chartConfig.title` on the bar chart and
  // `chartConfig.grafLegend` [sic] on the doughnut. `Tooltip` never fires on an unattended
  // display but was in the legacy `registerables` set, so it stays for parity.
  Title,
  Legend,
  Tooltip,
);
